/**
 * Ethereum Bridge Service
 *
 * Handles sendToCosmos transactions from Ethereum to Gravity Bridge.
 * This uses the Gravity Bridge contract on Ethereum mainnet.
 */

import type { SendToCosmosParams } from '@/types/bridge';
import { GRAVITY_BRIDGE_CONTRACT } from './bridge-messages';

// ============================================================================
// Constants
// ============================================================================

/**
 * GRAV ERC20 token contract address on Ethereum mainnet
 */
export const GRAV_ERC20_ADDRESS = '0x29b0068CF6978005bCd52F805010CA0F52c6F0';

/**
 * GRAV has 18 decimals on Ethereum
 */
export const GRAV_ERC20_DECIMALS = 18;

/**
 * Expected finality delay (100 blocks ~20 minutes)
 */
export const ETH_FINALITY_BLOCKS = 100;

/**
 * Average Ethereum block time in seconds
 */
export const ETH_BLOCK_TIME_SECS = 12;

// ============================================================================
// ABI Definitions
// ============================================================================

/**
 * ERC20 approve function ABI
 */
export const ERC20_APPROVE_ABI = [
  {
    constant: false,
    inputs: [
      { name: 'spender', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    name: 'approve',
    outputs: [{ name: '', type: 'bool' }],
    type: 'function',
  },
  {
    constant: true,
    inputs: [
      { name: 'owner', type: 'address' },
      { name: 'spender', type: 'address' },
    ],
    name: 'allowance',
    outputs: [{ name: '', type: 'uint256' }],
    type: 'function',
  },
  {
    constant: true,
    inputs: [{ name: 'account', type: 'address' }],
    name: 'balanceOf',
    outputs: [{ name: '', type: 'uint256' }],
    type: 'function',
  },
] as const;

/**
 * Gravity Bridge sendToCosmos function ABI
 */
export const GRAVITY_BRIDGE_ABI = [
  {
    inputs: [
      { name: '_tokenContract', type: 'address' },
      { name: '_destination', type: 'string' },
      { name: '_amount', type: 'uint256' },
    ],
    name: 'sendToCosmos',
    outputs: [],
    type: 'function',
    stateMutability: 'nonpayable',
  },
] as const;

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Ethereum provider (MetaMask or similar)
 */
export interface EthProvider {
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
}

/**
 * Result of an approval transaction
 */
export interface ApprovalResult {
  txHash: string;
  success: boolean;
}

/**
 * Result of a sendToCosmos transaction
 */
export interface SendToCosmosResult {
  txHash: string;
  success: boolean;
  blockNumber?: number;
}

/**
 * Transaction state for UI
 */
export enum EthTxState {
  IDLE = 'IDLE',
  CHECKING_ALLOWANCE = 'CHECKING_ALLOWANCE',
  APPROVING = 'APPROVING',
  APPROVAL_PENDING = 'APPROVAL_PENDING',
  SENDING = 'SENDING',
  SEND_PENDING = 'SEND_PENDING',
  WAITING_FINALITY = 'WAITING_FINALITY',
  COMPLETE = 'COMPLETE',
  ERROR = 'ERROR',
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Convert GRAV tokens to wei (18 decimals)
 */
export function gravToWei(grav: string | number | undefined): string {
  if (grav === undefined || grav === null || grav === '') return '0';
  const amount = typeof grav === 'string' ? parseFloat(grav) : grav;
  if (isNaN(amount) || amount <= 0) return '0';
  // Use BigInt for precise calculation
  // grav * 10^18
  const gravBigInt = BigInt(Math.floor(amount * 1_000_000));
  // Convert from 6 decimals to 18 decimals: multiply by 10^12
  const wei = gravBigInt * BigInt(1_000_000_000_000);
  return wei.toString();
}

/**
 * Convert wei (18 decimals) to GRAV tokens
 */
export function weiToGrav(wei: string | undefined, decimals = 6): string {
  if (wei === undefined || wei === '' || wei === '0' || wei.length === 0) return '0.000000'.slice(0, decimals + 2);
  try {
    const amount = BigInt(wei);
    // Convert from 18 decimals to 6 decimals: divide by 10^12
    const grav = amount / BigInt(1_000_000_000_000);
    const whole = grav / BigInt(1_000_000);
    const fraction = grav % BigInt(1_000_000);
    const fractionStr = fraction.toString().padStart(6, '0').slice(0, decimals);
    return `${whole}.${fractionStr}`;
  } catch (error) {
    console.error('Failed to convert wei to GRAV:', error);
    return '0.000000'.slice(0, decimals + 2);
  }
}

/**
 * Synchronous keccak256 using pre-computed function selectors
 * Pre-computed for common Gravity Bridge functions
 */
function keccak256(message: string): string {
  // Pre-computed function selectors
  const selectors: Record<string, string> = {
    'approve(address,uint256)': '0x095ea7b3',
    'allowance(address,address)': '0xdd62ed3e',
    'balanceOf(address)': '0x70a08231',
    'sendToCosmos(address,string,uint256)': '0x1ffbe7f9',
  };
  const selector = selectors[message];
  if (selector === undefined) {
    return '0x00000000';
  }
  return selector;
}

// ============================================================================
// Main Functions
// ============================================================================

/**
 * Check current ERC20 allowance for Gravity Bridge contract
 */
export async function checkAllowance(
  provider: EthProvider,
  owner: string,
  tokenContract: string = GRAV_ERC20_ADDRESS
): Promise<bigint> {
  const selector = keccak256('allowance(address,address)');
  const data =
    selector +
    owner.slice(2).toLowerCase().padStart(64, '0') +
    GRAVITY_BRIDGE_CONTRACT.slice(2).toLowerCase().padStart(64, '0');

  const result = await provider.request({
    method: 'eth_call',
    params: [
      {
        to: tokenContract,
        data,
      },
      'latest',
    ],
  });

  return BigInt(result as string);
}

/**
 * Check ERC20 balance
 */
export async function checkBalance(
  provider: EthProvider,
  account: string,
  tokenContract: string = GRAV_ERC20_ADDRESS
): Promise<bigint> {
  const selector = keccak256('balanceOf(address)');
  const data = selector + account.slice(2).toLowerCase().padStart(64, '0');

  const result = await provider.request({
    method: 'eth_call',
    params: [
      {
        to: tokenContract,
        data,
      },
      'latest',
    ],
  });

  return BigInt(result as string);
}

/**
 * Approve ERC20 token spending by Gravity Bridge contract
 */
export async function approveToken(
  provider: EthProvider,
  from: string,
  amount: string,
  tokenContract: string = GRAV_ERC20_ADDRESS
): Promise<ApprovalResult> {
  // approve(address spender, uint256 amount)
  const selector = keccak256('approve(address,uint256)');
  const data =
    selector +
    GRAVITY_BRIDGE_CONTRACT.slice(2).toLowerCase().padStart(64, '0') +
    BigInt(amount).toString(16).padStart(64, '0');

  const txHash = (await provider.request({
    method: 'eth_sendTransaction',
    params: [
      {
        from,
        to: tokenContract,
        data,
      },
    ],
  })) as string;

  return { txHash, success: true };
}

/**
 * Wait for transaction to be mined
 */
export async function waitForTransaction(
  provider: EthProvider,
  txHash: string,
  confirmations = 1
): Promise<{ blockNumber: number; success: boolean }> {
  // Poll for receipt
  let attempts = 0;
  const maxAttempts = 60; // 5 minutes with 5 second intervals

  while (attempts < maxAttempts) {
    const receipt = await provider.request({
      method: 'eth_getTransactionReceipt',
      params: [txHash],
    });

    if (receipt !== null) {
      const receiptObj = receipt as {
        blockNumber: string;
        status: string;
      };
      const blockNumber = parseInt(receiptObj.blockNumber, 16);
      const success = receiptObj.status === '0x1';

      if (confirmations <= 1) {
        return { blockNumber, success };
      }

      // Wait for additional confirmations
      const latestBlock = (await provider.request({
        method: 'eth_blockNumber',
        params: [],
      })) as string;
      const currentBlock = parseInt(latestBlock, 16);

      if (currentBlock - blockNumber >= confirmations - 1) {
        return { blockNumber, success };
      }
    }

    // Wait 5 seconds before polling again
    await new Promise((resolve) => setTimeout(resolve, 5000));
    attempts++;
  }

  throw new Error('Transaction not mined within timeout');
}

/**
 * Send tokens to Cosmos via Gravity Bridge
 */
export async function sendToCosmos(
  provider: EthProvider,
  from: string,
  params: SendToCosmosParams
): Promise<SendToCosmosResult> {
  // sendToCosmos(address _tokenContract, string _destination, uint256 _amount)
  const { token_contract, destination, amount } = params;

  // Build the calldata manually
  // Function selector
  const selector = keccak256('sendToCosmos(address,string,uint256)');

  // Encode parameters:
  // 1. address _tokenContract (32 bytes)
  // 2. offset to string data (32 bytes) - points to where string starts
  // 3. uint256 _amount (32 bytes)
  // 4. string length (32 bytes)
  // 5. string data (padded to 32-byte boundary)

  const tokenContractEncoded = token_contract.slice(2).toLowerCase().padStart(64, '0');
  const amountEncoded = BigInt(amount).toString(16).padStart(64, '0');

  // String offset: 3 * 32 = 96 bytes (0x60)
  const stringOffset = (3 * 32).toString(16).padStart(64, '0');

  // String length and data
  const destinationBytes = new TextEncoder().encode(destination);
  const stringLength = destinationBytes.length.toString(16).padStart(64, '0');

  let stringData = '';
  for (const byte of destinationBytes) {
    stringData += byte.toString(16).padStart(2, '0');
  }
  // Pad to 32-byte boundary
  const paddedLength = Math.ceil(destinationBytes.length / 32) * 64;
  stringData = stringData.padEnd(paddedLength, '0');

  const data =
    selector +
    tokenContractEncoded +
    stringOffset +
    amountEncoded +
    stringLength +
    stringData;

  const txHash = (await provider.request({
    method: 'eth_sendTransaction',
    params: [
      {
        from,
        to: GRAVITY_BRIDGE_CONTRACT,
        data,
      },
    ],
  })) as string;

  return { txHash, success: true };
}

/**
 * Get current Ethereum block number
 */
export async function getCurrentBlockNumber(provider: EthProvider): Promise<number> {
  const result = (await provider.request({
    method: 'eth_blockNumber',
    params: [],
  })) as string;
  return parseInt(result, 16);
}

/**
 * Calculate estimated finality time
 */
export function calculateFinalityTime(currentBlock: number, txBlock: number): {
  blocksRemaining: number;
  secondsRemaining: number;
  isFinalized: boolean;
} {
  const blocksRemaining = Math.max(0, ETH_FINALITY_BLOCKS - (currentBlock - txBlock));
  const secondsRemaining = blocksRemaining * ETH_BLOCK_TIME_SECS;
  const isFinalized = blocksRemaining === 0;

  return { blocksRemaining, secondsRemaining, isFinalized };
}

/**
 * Execute full ETH → Gravity deposit flow
 * Handles approval check, approval (if needed), and sendToCosmos
 */
export async function executeDeposit(
  provider: EthProvider,
  from: string,
  destination: string,
  amountWei: string,
  onStateChange?: (state: EthTxState, txHash?: string) => void
): Promise<SendToCosmosResult> {
  const tokenContract = GRAV_ERC20_ADDRESS;

  try {
    // 1. Check current allowance
    onStateChange?.(EthTxState.CHECKING_ALLOWANCE);
    const currentAllowance = await checkAllowance(provider, from, tokenContract);
    const amountBigInt = BigInt(amountWei);

    // 2. Approve if needed
    if (currentAllowance < amountBigInt) {
      onStateChange?.(EthTxState.APPROVING);
      const approvalResult = await approveToken(provider, from, amountWei, tokenContract);

      onStateChange?.(EthTxState.APPROVAL_PENDING, approvalResult.txHash);
      const approvalReceipt = await waitForTransaction(provider, approvalResult.txHash);

      if (!approvalReceipt.success) {
        throw new Error('Approval transaction failed');
      }
    }

    // 3. Send to Cosmos
    onStateChange?.(EthTxState.SENDING);
    const sendResult = await sendToCosmos(provider, from, {
      token_contract: tokenContract,
      destination,
      amount: amountWei,
    });

    onStateChange?.(EthTxState.SEND_PENDING, sendResult.txHash);
    const sendReceipt = await waitForTransaction(provider, sendResult.txHash);

    if (!sendReceipt.success) {
      throw new Error('sendToCosmos transaction failed');
    }

    onStateChange?.(EthTxState.WAITING_FINALITY, sendResult.txHash);

    return {
      txHash: sendResult.txHash,
      success: true,
      blockNumber: sendReceipt.blockNumber,
    };
  } catch (error) {
    onStateChange?.(EthTxState.ERROR);
    throw error;
  }
}
